import { generatePath } from 'react-router-dom'
import { CompanyTag } from '@/components/ui/badge'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { TransactionKey } from 'api/Transaction/Types/Transaction'
import { Link } from 'components/Link'
import { generateClassNameForReverseType } from 'helpers/transactions'
import { formatLocalDateUTC } from 'utils/date'
import { isEmpty } from 'utils/is-empty'

import './Create/transaction.scss'

import {
  AdjustmentTransactionDto,
  AdjustmentTransactionKey,
} from 'api/Transaction/Types/AdjustmentTransactionDto'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

const ReferenceCell = (props: GridCellProps): JSX.Element => {
  if (isEmpty(props.dataItem.referenceId)) {
    return <td className={generateClassNameForReverseType(props.dataItem)} />
  }

  return (
    <td
      className={generateClassNameForReverseType(props.dataItem)}
      style={{ color: 'var(--link)', textDecoration: 'underline' }}
    >
      <Link
        to={{
          pathname: generatePath('/transactions/:id', {
            id: props.dataItem.id,
          }),
        }}
      >
        {props.dataItem.referenceId}
      </Link>
    </td>
  )
}

export const transactionTradingAccountAdjustmentsColumns: GridColumn[] = [
  {
    title: 'Reference',
    field: TransactionKey.reference,
    cell: ReferenceCell,
    show: true,
    filter: 'text',
  },
  {
    title: 'Type',
    field: TransactionKey.type,
    cell: (props: GridCellProps): JSX.Element => {
      const type = props.dataItem.type.name
      return <td className={generateClassNameForReverseType(props.dataItem, 'number')}>{type}</td>
    },
    show: true,
  },
  {
    title: '# of Clients',
    field: TransactionKey.clientCount,
    cell: (props: GridCellProps): JSX.Element => {
      const clientCount = props.dataItem.clientCount
      if (isEmpty(clientCount)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return <td className={generateClassNameForReverseType(props.dataItem)}>{clientCount}</td>
    },
    show: true,
    width: 120,
  },
  {
    title: '# of Trading Accounts',
    field: AdjustmentTransactionKey.tradingAccountCount,
    cell: (props: GridCellProps): JSX.Element => {
      const { tradingAccountCount } = props.dataItem as AdjustmentTransactionDto

      if (isEmpty(tradingAccountCount)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>{tradingAccountCount}</td>
      )
    },
    show: true,
  },
  {
    title: 'Amount',
    field: TransactionKey.amount,
    cell: (props: GridCellProps): JSX.Element => {
      const amount = props.dataItem.amount
      if (isEmpty(amount)) {
        return <td className={generateClassNameForReverseType(props.dataItem, 'number')} />
      }
      return (
        <td className={generateClassNameForReverseType(props.dataItem, 'number')}>
          {amount.toFixed(2)}
        </td>
      )
    },
    resizable: true,
    show: true,
    filter: 'text',
  },
  {
    title: 'Currency',
    field: TransactionKey.currency,
    cell: (props: GridCellProps): JSX.Element => {
      const currency = props.dataItem.currency
      if (isEmpty(currency)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      const currencyId = props.dataItem.currency.id
      if (isEmpty(currencyId)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return <td className={generateClassNameForReverseType(props.dataItem)}>{currencyId}</td>
    },
    resizable: true,
    show: true,
    filter: 'text',
    width: 80,
  },
  {
    title: 'Status',
    field: TransactionKey.state,
    cell: (props: GridCellProps): JSX.Element => {
      const status = props.dataItem.state
      if (isEmpty(status)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return <td className={generateClassNameForReverseType(props.dataItem)}>{status.name}</td>
    },
    show: true,
    className: 'transaction-columns__content',
    filter: 'text',
  },

  {
    title: 'Entity',
    field: TransactionKey.tickmillCompany,
    cell: (props: GridCellProps): JSX.Element => {
      const tickmillCompany = props.dataItem.tickmillCompany
      if (!tickmillCompany) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      const company = props.dataItem.tickmillCompany
      if (!company) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          <CompanyTag id={company.id}>{company.name}</CompanyTag>
        </td>
      )
    },
    resizable: true,
    show: true,
    className: 'transaction-columns__content',
    filter: 'text',
    width: 80,
    minResizableWidth: 80,
  },
  {
    title: 'Date Created',
    field: TransactionKey.createdDate,
    cell: (props: GridCellProps) => (
      <td className={generateClassNameForReverseType(props.dataItem, 'date')}>
        {formatLocalDateUTC(props.dataItem.createdDate, true)}
      </td>
    ),
    show: true,
    resizable: true,
    filter: 'text',
  },
  {
    title: 'Date Updated',
    field: TransactionKey.updatedDate,
    cell: (props: GridCellProps) => (
      <td className={generateClassNameForReverseType(props.dataItem, 'date')}>
        {formatLocalDateUTC(props.dataItem.updatedDate, true)}
      </td>
    ),
    show: true,
    resizable: true,
    filter: 'text',
  },
]
